// Shared helpers for the Data setup tabs — only what BOTH tabs duplicated
// verbatim. The draft stores, baselines and save handlers are deliberately NOT
// here: they look alike but differ in every load-bearing detail (key shape,
// baseline source, value resolution, pruning), and one abstraction over both is
// how the split(':')-style bugs get reintroduced.

import { useState } from 'react';
import type { ApiErrorShape } from '@/api/types';

/** Page/limit state plus the reset-to-1 rule both tabs repeat. The window
 *  arithmetic is `pageRange` beside it — a pure function, since `total` only
 *  exists after the query this state feeds. */
export const useTablePaging = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const onLimit = (n: number) => {
    setLimit(n);
    setPage(1);
  };
  return { page, limit, setPage, onLimit };
};

/** The footer's "{from}–{to} of {total}" window. */
export const pageRange = (page: number, limit: number, total: number) => ({
  from: total === 0 ? 0 : (page - 1) * limit + 1,
  to: Math.min(page * limit, total),
});

/** { added, removed } of the user's picks vs the saved baseline — the
 *  update-metadata delta both tabs send. */
export const setsDelta = (enabled: Set<string>, saved: Set<string>) => ({
  added: [...enabled].filter((id) => !saved.has(id)),
  removed: [...saved].filter((id) => !enabled.has(id)),
});

/** Same members, ignoring identity — so a fresh Set off each response doesn't
 *  churn renders through every memo that depends on one. */
export const sameIds = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every((id) => b.has(id));

/** The server's own message when it sent one, else the given fallback — the
 *  module's standing toast idiom, minus the per-call-site cast. */
export const saveErrorMsg = (err: unknown, fallback: string) =>
  (err as ApiErrorShape)?.msg ?? fallback;
